// Retarget lost leads — message drafts (client, 2026-11-04).
//
// Admin / Super Admin: tick one or more lost reasons, draft the WhatsApp
// message that will win those clients back, and tick exactly which clients it
// is for. Nothing is sent from this page — sending comes later, through
// Interakt approved Marketing templates. "Template for Interakt" is the body
// to submit there. A reason can be in one draft only.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Copy, Megaphone, Plus, Save, Search, Trash2 } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { cn } from '@/shared/utils/utils';

interface Reason {
  reasonKey: string; name: string; active: boolean; excluded: boolean; records: number; mobiles: number;
  draft: { id: string; title: string } | null;
}
interface ReasonsResponse { items: Reason[]; totals: { records: number; retargetable: number }; placeholders: string[]; maxMessage: number }
type AudienceMode = 'all_except' | 'only';
interface Draft {
  id: string; title: string; reasonKeys: string[]; reasonNames: string[]; message: string;
  audience: { days: number; mode: AudienceMode; ids: string[] };
  updatedByName: string; updatedAt: string; template: { body: string; variables: string[] }; hasOptOut: boolean;
  audienceSize: number | null; selected: number | null;
}
interface Client { id: string; phone: string; lostOn: string | null; reason: string; lostNote: string; values: Record<string, string> }

const PERIODS = [
  { days: 0, label: 'All time' }, { days: 30, label: 'Last 30 days' }, { days: 90, label: 'Last 90 days' }, { days: 180, label: 'Last 6 months' },
];
const FIELD_HINT: Record<string, string> = {
  name: "client's name", company: 'company', product: 'what they enquired for', salesperson: 'their salesperson',
};
const NEW = 'new';

/** Named fields → {{1}}, {{2}}… in order of first use (same rule as the server). */
function toTemplate(message: string) {
  const order: string[] = [];
  const body = message.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, raw: string) => {
    const k = raw.toLowerCase();
    if (!order.includes(k)) order.push(k);
    return `{{${order.indexOf(k) + 1}}}`;
  });
  return { body, variables: order };
}
const render = (message: string, values: Record<string, string>) =>
  message.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (whole, raw: string) => values[raw.toLowerCase()] || whole);
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export function RetargetDraftsPage() {
  const qc = useQueryClient();
  const [days, setDays] = useState(0);
  const [openId, setOpenId] = useState<string>(NEW);
  const [keys, setKeys] = useState<string[]>([]);
  // Changes only when a draft (or New) is opened — ticking reasons or saving
  // never resets what is being typed.
  const [editorKey, setEditorKey] = useState(0);

  const reasons = useQuery({
    queryKey: ['retarget', 'reasons', days],
    queryFn: async () => (await api.get<ReasonsResponse>(`/api/retarget/reasons${days ? `?days=${days}` : ''}`)).data,
    placeholderData: (prev) => prev,
  });
  const drafts = useQuery({
    queryKey: ['retarget', 'drafts'],
    queryFn: async () => (await api.get<{ items: Draft[] }>('/api/retarget/drafts')).data.items,
  });
  const openDraft = (drafts.data ?? []).find((d) => d.id === openId);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['retarget'] }); };

  const open = (d: Draft | null) => {
    setOpenId(d ? d.id : NEW);
    setKeys(d ? d.reasonKeys : []);
    setEditorKey((k) => k + 1);
    if (d) setDays(d.audience?.days || 0);
  };
  const toggleReason = (r: Reason) => {
    if (r.excluded || (r.draft && r.draft.id !== openId)) return;
    setKeys((k) => (k.includes(r.reasonKey) ? k.filter((x) => x !== r.reasonKey) : [...k, r.reasonKey]));
  };
  const items = reasons.data?.items ?? [];

  return (
    <div className="space-y-4 pb-16">
      <PageHeader
        icon={Megaphone}
        eyebrow="Sales"
        title="Retarget Lost Leads"
        description={<>Tick one or more lost reasons, draft the WhatsApp message to win those clients back, and choose exactly who it is for.
          Nothing is sent from here — the message is ready to submit to Interakt as a Marketing template when sending is switched on.</>}
      />

      {/* Saved drafts */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={openId === NEW ? 'secondary' : 'ghost'} onClick={() => open(null)}><Plus className="mr-1 h-4 w-4" /> New draft</Button>
        {(drafts.data ?? []).map((d) => (
          <button key={d.id} type="button" onClick={() => open(d)}
            className={cn('rounded-full border px-3 py-1 text-xs font-medium', openId === d.id ? 'border-primary bg-primary text-on-primary' : 'border-outline text-on-surface-variant hover:border-primary')}>
            {d.title || d.reasonNames.join(' + ')}
            {d.selected !== null && <span className="ml-1.5 opacity-80">{d.selected} clients</span>}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card>
          <CardHeader title="Lost reasons" />
          <CardBody className="space-y-2 p-2">
            <select aria-label="Lost in" className="h-8 w-full rounded border border-outline bg-surface px-2 text-xs" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {PERIODS.map((p) => <option key={p.days} value={p.days}>Lost in: {p.label}</option>)}
            </select>
            {reasons.isLoading ? <Skeleton className="h-64" /> : (
              <ul className="space-y-1">
                {items.map((r) => {
                  const taken = Boolean(r.draft && r.draft.id !== openId);
                  const blocked = r.excluded || taken;
                  return (
                    <li key={r.reasonKey}>
                      <label className={cn('flex items-start gap-2 rounded-lg px-2 py-2 text-sm',
                        blocked ? 'cursor-not-allowed opacity-55' : 'cursor-pointer hover:bg-muted',
                        keys.includes(r.reasonKey) && 'bg-primary/10 ring-1 ring-primary/30')}
                        title={r.excluded ? 'Left out of retargeting' : taken ? `Already in the draft "${r.draft!.title || 'untitled'}"` : undefined}>
                        <input type="checkbox" className="mt-0.5" disabled={blocked} checked={keys.includes(r.reasonKey)} onChange={() => toggleReason(r)} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{r.name}</span>
                          <span className="text-[11px] text-on-surface-variant">
                            {r.excluded ? 'Left out of retargeting' : `${r.mobiles} mobiles · ${r.records} records`}
                            {taken && <> · in “{r.draft!.title || 'another draft'}”</>}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {reasons.data && (
              <p className="px-2 text-[11px] text-on-surface-variant">
                {reasons.data.totals.retargetable} mobiles to retarget in all (Irrelevant left out). A reason can be in one draft only.
              </p>
            )}
          </CardBody>
        </Card>

        {keys.length ? (
          <DraftEditor
            key={editorKey}
            draft={openDraft}
            reasonKeys={keys}
            reasonNames={keys.map((k) => items.find((i) => i.reasonKey === k)?.name || k)}
            days={days}
            placeholders={reasons.data?.placeholders ?? []}
            maxMessage={reasons.data?.maxMessage ?? 1024}
            onSaved={(d) => { refresh(); setOpenId(d.id); }}
            onDeleted={() => { refresh(); open(null); }}
          />
        ) : (
          <Card><CardBody className="p-10 text-center text-sm text-on-surface-variant">
            {openId === NEW ? 'Tick one or more lost reasons to draft a message for them.' : 'This draft has no lost reasons ticked.'}
          </CardBody></Card>
        )}
      </div>
    </div>
  );
}

function DraftEditor({ draft, reasonKeys, reasonNames, days, placeholders, maxMessage, onSaved, onDeleted }: {
  draft?: Draft; reasonKeys: string[]; reasonNames: string[]; days: number; placeholders: string[]; maxMessage: number;
  onSaved: (d: Draft) => void; onDeleted: () => void;
}) {
  const reasonParam = encodeURIComponent(reasonKeys.join(','));
  const audience = useQuery({
    queryKey: ['retarget', 'audience', reasonParam, days],
    queryFn: async () => (await api.get<{ clients: Client[] }>(`/api/retarget/audience?reasons=${reasonParam}${days ? `&days=${days}` : ''}`)).data.clients,
  });
  const suggested = useQuery({
    queryKey: ['retarget', 'suggested', reasonParam],
    queryFn: async () => (await api.get<{ suggested: string }>(`/api/retarget/suggested?reasons=${reasonParam}`)).data.suggested,
  });

  const [title, setTitle] = useState(draft?.title || '');
  const [message, setMessage] = useState(draft?.message || '');
  // Who it is for. 'all_except' + unticked ids, or 'only' + ticked ids.
  const [mode, setMode] = useState<AudienceMode>(draft?.audience?.mode || 'all_except');
  const [ids, setIds] = useState<Set<string>>(new Set(draft?.audience?.ids || []));
  const [search, setSearch] = useState('');
  const [previewId, setPreviewId] = useState('');
  // The saved draft this editor writes to — set by the first save of a new one.
  const [savedId, setSavedId] = useState(draft?.id || '');
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!draft && !message && suggested.data) setMessage(suggested.data);
  }, [draft, message, suggested.data]);

  const clients = audience.data ?? [];
  const isSelected = (id: string) => (mode === 'only' ? ids.has(id) : !ids.has(id));
  const selected = clients.filter((c) => isSelected(c.id)).length;
  const toggle = (id: string) => setIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = () => { setMode('all_except'); setIds(new Set()); };
  const selectNone = () => { setMode('only'); setIds(new Set()); };
  const q = search.trim().toLowerCase();
  const shown = q ? clients.filter((c) => [c.values.name, c.values.company, c.values.product, c.values.salesperson, c.reason, c.phone].some((v) => String(v || '').toLowerCase().includes(q))) : clients;
  const previewClient = clients.find((c) => c.id === previewId) || clients.find((c) => isSelected(c.id)) || clients[0];

  const save = useMutation({
    mutationFn: async () => {
      const body = { title, message, reasonKeys, audience: { days, mode, ids: [...ids] } };
      const { data } = savedId ? await api.put(`/api/retarget/drafts/${savedId}`, body) : await api.post('/api/retarget/drafts', body);
      return data.draft as Draft;
    },
    onSuccess: (d) => { setSavedId(d.id); toast.success(`Draft saved — ${d.selected ?? selected} clients.`); onSaved(d); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the draft.')),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/api/retarget/drafts/${savedId}`)).data,
    onSuccess: () => { toast.success('Draft deleted.'); onDeleted(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not delete the draft.')),
  });

  const insert = (field: string) => {
    const el = box.current;
    const token = `{{${field}}}`;
    if (!el) { setMessage((m) => `${m}${token}`); return; }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    setMessage(message.slice(0, start) + token + message.slice(end));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };

  const template = useMemo(() => toTemplate(message), [message]);
  const tooLong = message.length > maxMessage;
  const optOut = /\bSTOP\b/.test(message);
  const unknown = [...new Set([...message.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/gi)].map((m) => m[1].toLowerCase()).filter((p) => !placeholders.includes(p)))];
  const canSave = Boolean(message.trim()) && !tooLong && !unknown.length && selected > 0 && !save.isPending;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={reasonNames.join(' + ')}
          action={draft ? <span className="text-xs text-on-surface-variant">Saved by {draft.updatedByName || '—'} · {new Date(draft.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span> : <Badge className="bg-muted text-on-surface-variant">New draft</Badge>}
        />
        <CardBody className="space-y-3">
          <label className="block text-xs font-semibold text-on-surface-variant">Name for this draft (for you, not sent)
            <input className="mt-1 h-9 w-full rounded border border-outline bg-surface px-2 text-sm" maxLength={120}
              placeholder={`e.g. ${reasonNames[0]} — November offer`} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-on-surface-variant">Insert:</span>
              {placeholders.map((p) => (
                <button key={p} type="button" onClick={() => insert(p)} title={FIELD_HINT[p] || p}
                  className="rounded border border-outline px-2 py-0.5 font-mono text-[11px] hover:border-primary hover:text-primary">{`{{${p}}}`}</button>
              ))}
            </div>
            <textarea ref={box} rows={6} value={message} onChange={(e) => setMessage(e.target.value)}
              className={cn('w-full rounded border bg-surface px-3 py-2 text-sm', tooLong ? 'border-danger' : 'border-outline')} />
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px]">
              <span className={tooLong ? 'font-semibold text-danger-text' : 'text-on-surface-variant'}>{message.length} / {maxMessage} characters</span>
              <span className="flex flex-wrap gap-3">
                {optOut
                  ? <span className="inline-flex items-center gap-1 text-success-text"><CheckCircle2 className="h-3 w-3" /> Has a STOP opt-out</span>
                  : <span className="inline-flex items-center gap-1 text-warning-text"><AlertTriangle className="h-3 w-3" /> Add an opt-out line, e.g. “Reply STOP to stop these messages” — Meta expects it</span>}
                {unknown.length > 0 && <span className="text-danger-text">Unknown: {unknown.map((u) => `{{${u}}}`).join(', ')}</span>}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">For <b>{selected}</b> of {clients.length} clients</span>
            <div className="flex flex-wrap gap-2">
              {savedId && (
                <Button size="sm" variant="ghost" disabled={remove.isPending}
                  onClick={() => { if (window.confirm(`Delete the draft "${title || reasonNames.join(' + ')}"?`)) remove.mutate(); }}>
                  <Trash2 className="mr-1 h-4 w-4" /> Delete
                </Button>
              )}
              {suggested.data && message !== suggested.data && (
                <Button size="sm" variant="ghost" onClick={() => setMessage(suggested.data!)}>Use suggested text</Button>
              )}
              <Button size="sm" disabled={!canSave} title={selected === 0 ? 'Select at least one client' : undefined} onClick={() => save.mutate()}>
                <Save className="mr-1 h-4 w-4" /> {save.isPending ? 'Saving…' : 'Save draft'}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Who it is for */}
      <Card>
        <CardHeader
          title={`Clients — ${selected} selected`}
          action={(
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-on-surface-variant" />
                <input type="search" aria-label="Search clients" placeholder="Search name, company, product…" value={search} onChange={(e) => setSearch(e.target.value)}
                  className="h-8 w-56 rounded border border-outline bg-surface pl-7 pr-2 text-xs" />
              </div>
              <Button size="sm" variant="ghost" onClick={selectAll}>Select all</Button>
              <Button size="sm" variant="ghost" onClick={selectNone}>Select none</Button>
            </div>
          )}
        />
        <CardBody className="p-0">
          {audience.isLoading ? <div className="p-4"><Skeleton className="h-40" /></div> : !clients.length ? (
            <p className="p-6 text-sm text-on-surface-variant">No clients in {reasonKeys.length > 1 ? 'these reasons' : 'this reason'}{days ? ' for this period' : ''}.</p>
          ) : (
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="sticky top-0 bg-muted text-left text-[11px] uppercase text-on-surface-variant">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" aria-label="Select all shown" checked={shown.length > 0 && shown.every((c) => isSelected(c.id))}
                        onChange={(e) => {
                          const want = e.target.checked;
                          setIds((s) => {
                            const n = new Set(s);
                            for (const c of shown) {
                              // In 'only' the set holds the ticked; in 'all_except' the unticked.
                              const inSet = mode === 'only' ? want : !want;
                              if (inSet) n.add(c.id); else n.delete(c.id);
                            }
                            return n;
                          });
                        }} />
                    </th>
                    <th className="px-3 py-2">Client</th>
                    <th className="px-3 py-2">Enquired for</th>
                    <th className="px-3 py-2">Reason</th>
                    <th className="px-3 py-2">Salesperson</th>
                    <th className="px-3 py-2">Lost on</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id} className={cn('border-t', !isSelected(c.id) && 'text-on-surface-variant opacity-60')}>
                      <td className="px-3 py-2"><input type="checkbox" aria-label={`Select ${c.values.company}`} checked={isSelected(c.id)} onChange={() => toggle(c.id)} /></td>
                      <td className="px-3 py-2"><div className="font-medium">{c.values.company || c.values.name}</div><div className="text-[11px] text-on-surface-variant">{c.values.name !== c.values.company ? `${c.values.name} · ` : ''}{c.phone}</div></td>
                      <td className="px-3 py-2 text-xs">{c.values.product || '—'}</td>
                      <td className="px-3 py-2 text-xs">{c.reason}</td>
                      <td className="px-3 py-2 text-xs">{c.values.salesperson}</td>
                      <td className="px-3 py-2 text-xs">{when(c.lostOn)}</td>
                      <td className="px-3 py-2 text-right"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setPreviewId(c.id)}>Preview</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={previewClient ? `Preview — ${previewClient.values.company || previewClient.values.name}` : 'Preview'} />
          <CardBody>
            {!previewClient ? <p className="text-sm text-on-surface-variant">No client to preview.</p> : (
              <>
                <div className="whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-success/10 px-4 py-3 text-sm text-on-surface">{render(message, previewClient.values)}</div>
                <p className="mt-2 text-[11px] text-on-surface-variant">
                  {previewClient.phone} · {previewClient.reason} · lost {when(previewClient.lostOn)}{previewClient.lostNote ? ` · note: “${previewClient.lostNote}”` : ''}
                </p>
              </>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="Template for Interakt"
            action={<Button size="sm" variant="ghost" onClick={() => { void navigator.clipboard?.writeText(template.body); toast.success('Template text copied.'); }}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>}
          />
          <CardBody className="space-y-2">
            <pre className="whitespace-pre-wrap rounded bg-muted px-3 py-2 font-sans text-sm">{template.body || '—'}</pre>
            {template.variables.length > 0 && (
              <p className="text-[11px] text-on-surface-variant">{template.variables.map((v, i) => `{{${i + 1}}} = ${FIELD_HINT[v] || v}`).join(' · ')}</p>
            )}
            <p className="text-[11px] text-on-surface-variant">Submit this in Interakt as a <b>Marketing</b> template. Meta approves it before it can be sent.</p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default RetargetDraftsPage;
